import React from 'react';
import { ArrowLeft, AlertTriangle, FileText, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';

function Disclaimer() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">Disclaimer</h1>
          </div>
          <p className="text-slate-600 text-sm">Last Updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* General Disclaimer */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">General Disclaimer</h2>
            <p className="text-slate-700 leading-relaxed">
              The information provided by BrightBazaar.in (operated by BrightPlanet Ventures) on our 
              website and through our services is for general informational purposes only. All information 
              is provided in good faith, however we make no representation or warranty of any kind, express 
              or implied, regarding the accuracy, adequacy, validity, reliability, availability, or 
              completeness of any information on the website.
            </p>
          </section>

          {/* Website Content */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <FileText className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Website Content</h2>
                <div className="space-y-4 text-slate-700">
                  <p>
                    While we strive to keep the information on our website accurate and up-to-date, 
                    BrightBazaar.in makes no warranties or representations as to the accuracy or 
                    completeness of product descriptions, images, pricing, specifications, availability, 
                    or any other content on the website.
                  </p>
                  <p>
                    Product images are for illustrative purposes only. Actual products may vary slightly 
                    in appearance, color, or design due to manufacturing variations, display settings, 
                    and photographic processes.
                  </p>
                  <p>
                    We reserve the right to modify, update, or discontinue any content, products, or 
                    services at any time without prior notice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Product Information */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Product Information Disclaimer</h2>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                <span className="text-orange-600 font-bold shrink-0">•</span>
                <div>
                  <strong>Product Specifications:</strong> Technical specifications, dimensions, and 
                  features are provided by manufacturers and may be subject to change without notice.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                <span className="text-orange-600 font-bold shrink-0">•</span>
                <div>
                  <strong>Pricing:</strong> All prices are subject to change without notice. While we 
                  make every effort to ensure pricing accuracy, errors may occur. We reserve the right 
                  to correct pricing errors and cancel orders placed at incorrect prices.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                <span className="text-orange-600 font-bold shrink-0">•</span>
                <div>
                  <strong>Availability:</strong> Product availability is not guaranteed. Items may be 
                  sold out or discontinued without prior notice.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                <span className="text-orange-600 font-bold shrink-0">•</span>
                <div>
                  <strong>Color Variations:</strong> Colors displayed on your screen may differ from 
                  actual product colors due to monitor settings and display technologies.
                </div>
              </li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Scale className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Limitation of Liability</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Under no circumstance shall BrightBazaar.in or BrightPlanet Ventures have any 
                  liability to you for any loss or damage of any kind incurred as a result of the 
                  use of the website or reliance on any information provided on the website.
                </p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Your use of the website and your reliance on any information on the website is 
                  solely at your own risk.
                </p>
                <div className="bg-orange-50 rounded-lg p-4 mt-4">
                  <p className="text-slate-700 text-sm">
                    <strong>Important:</strong> BrightBazaar.in shall not be liable for any indirect, 
                    consequential, or special liability arising out of or in any way related to your 
                    use of the website, products, or services.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Third-Party Links and Content</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Our website may contain links to third-party websites or services that are not owned or 
              controlled by BrightBazaar.in. We have no control over, and assume no responsibility for, 
              the content, privacy policies, or practices of any third-party websites or services.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We strongly advise you to read the terms and conditions and privacy policies of any 
              third-party websites or services that you visit.
            </p>
          </section>

          {/* Professional Advice */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Professional Advice Disclaimer</h2>
            <p className="text-slate-700 leading-relaxed">
              The website cannot and does not contain professional advice. The information is provided 
              for general informational and educational purposes only and is not a substitute for 
              professional advice. Accordingly, before taking any actions based upon such information, 
              we encourage you to consult with the appropriate professionals.
            </p>
          </section>

          {/* Warranty Disclaimer */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Warranty Disclaimer</h2>
            <div className="space-y-3 text-slate-700">
              <p>
                Products are sold "as is" and "as available" unless otherwise specified. While we 
                strive to offer quality products, we disclaim all warranties, express or implied, 
                including but not limited to:
              </p>
              <ul className="space-y-2 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 mt-1">→</span>
                  <span>Implied warranties of merchantability</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 mt-1">→</span>
                  <span>Fitness for a particular purpose</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-600 mt-1">→</span>
                  <span>Non-infringement of intellectual property rights</span>
                </li>
              </ul>
              <p className="mt-4">
                Manufacturer warranties, where applicable, are separate and independent from 
                BrightBazaar.in. Please refer to the manufacturer's warranty terms for specific coverage.
              </p>
            </div>
          </section>

          {/* Technical Issues */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Technical Issues</h2>
            <p className="text-slate-700 leading-relaxed">
              BrightBazaar.in does not guarantee that the website will be secure, error-free, or 
              uninterrupted, or that defects will be corrected. We do not guarantee that the website 
              or any content, service, or feature of the website will function on any particular 
              hardware or device. The website may be subject to malfunctions and delays inherent in 
              the use of the internet and electronic communications.
            </p>
          </section>

          {/* Testimonials */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Testimonials Disclaimer</h2>
            <p className="text-slate-700 leading-relaxed">
              Customer reviews and testimonials on the website reflect the individual experiences of 
              those customers and do not necessarily represent what others may experience with our 
              products or services. We do not guarantee that all customers will have similar experiences.
            </p>
          </section>

          {/* Errors and Omissions */}
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">Errors and Omissions</h2>
            <p className="text-slate-700 leading-relaxed">
              While we have made every attempt to ensure that the information contained on the website 
              is correct, BrightBazaar.in is not responsible for any errors or omissions, or for the 
              results obtained from the use of this information. We reserve the right to make changes 
              and correct errors without prior notice.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Governing Law</h2>
            <p className="text-slate-700 leading-relaxed">
              This disclaimer shall be governed by and construed in accordance with the laws of India. 
              Any disputes arising under or in connection with this disclaimer shall be subject to the 
              exclusive jurisdiction of the courts of Karnataka, India.
            </p>
          </section>

          {/* Contact Section */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Questions About This Disclaimer?</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions about this disclaimer, please contact us:
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-slate-700"><strong>Email:</strong> info@brightplanetventures.com</p>
              <p className="text-slate-700"><strong>Phone:</strong> +91 73532 97211</p>
              <p className="text-slate-700"><strong>Address:</strong> Mulla Complex, Somwar Pethe, Near MalaPrabha Hospital, Bailhongal - 591102, Karnataka, India</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default Disclaimer;
